import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Offcanvas } from "react-bootstrap";

import InfoTooltip from "src/components/tooltip";
import InfiniteScroll from "src/components/infiniteScroll";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
} from "recharts";
import dateformat from "dateformat";

import { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { intializeDownloadReport, invalidateReportsDownloadData } from "src/redux/actions/reports";
import DownloadReports from "../downloadReports";
import userRoleService from "src/utils/userRoles.service";
import { PropertyType } from "src/types/clusterFilters";

import _ from "lodash";
import cx from "classnames";
import styles from "./entityInsightPanel.module.scss";
import { EntitiesFilterTooltip } from "../entitiesFilterTooltip";
import { DiscoveryTables, EngagementTables, PerformanceTables, ReportTabs } from "src/types/reports";

export interface Filter {
  property: {
    type: PropertyType;
  };
  operator: string;
  value: any[];
}

interface entityInsightPanelProps {
  title?: string;
  description?: string;
  data?: ChartDataItem[];
  legend?: any;
  valueFormatter?: (value: any) => string;
  containerClassName?: string;
  loading?: boolean;
  scrollTitle?: string;
  items?: any[];
  columns: string[];
  renderScrollItem?: (item: any) => React.ReactNode;
  loadMore: () => void;
  pagination?: PaginationState;
  titleColumnName?: string;
  downloadConfig?: {
    name: string;
    schema: any;
    fileName: string;
    title: string;
    reportType: string;
  };
  firstColumn?: string;
  reportDate?: ReportDate;
  viewMore?: () => void;
  filters: Filter[];
  tabName: ReportTabs;
  tableName: DiscoveryTables | EngagementTables | PerformanceTables;
  graphLoading?: boolean;
}

interface DownloadConfig {
  schema: any[];
  fileName: string;
  title: string;
  reportType: string;
  actionId?: string;
  data?: any;
  pagination?: any;
  fetchState?: any;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

interface ChartDataItem extends Record<string, string | number> {
  key: string;
  value: number;
  cumulative: number;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
}

const EntityInsightPanel: React.FC<entityInsightPanelProps> = ({
  title,
  description,
  data,
  legend,
  valueFormatter,
  containerClassName,
  loading,
  scrollTitle,
  items,
  renderScrollItem,
  columns,
  loadMore,
  pagination,
  titleColumnName,
  downloadConfig,
  firstColumn,
  reportDate,
  viewMore,
  filters,
  tabName,
  tableName,
  graphLoading
}) => {
  const [openScrollList, setOpenScrollList] = useState(false);
  const dispatch = useDispatch();

  const handleLocalToggle = () => {
    setOpenScrollList((prev) => !prev);
    viewMore?.();
  };

  const handleCancelClick = () => {
    setOpenScrollList(false);
  };

  const { download } = useSelector((state: any) => ({
    download: _.get(state, "reports.download", {}),
  }));

  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor", "#e51058"),
  );

  const [activeDownloadConfig, setActiveDownloadConfig] = useState<
    typeof downloadConfig | null
  >(null);

  const fetchState = {
    loading: download.loading,
    succeeded: download.succeeded,
    error: download.error,
  };

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: "day",
    };
  }, [reportDate,filters]);

  const handleDownloadClick = () => {
    if (!downloadConfig || !baseFilters) return;
    const request = baseFilters;

    dispatch(
      intializeDownloadReport({
        id: downloadConfig.name,
        request,
        page: 0,
        limit: 5,
      }),
    );
    setActiveDownloadConfig(downloadConfig);
  };

  const invalidateDownloadData = () => {
    setActiveDownloadConfig(null);
    dispatch(invalidateReportsDownloadData())
  };

  return (
    <div className={cx(styles.container, containerClassName)}>
      <div className={styles.card}>
        <div className={styles.report}>
          <div className={styles.content}>
            <div className={styles.textContainer}>
              <div className={styles.title}>{title}</div>
              <InfoTooltip placement="top" content={description}>
                <i className={cx(styles.paperfliteInfo, "paperflite-info")}></i>
              </InfoTooltip>
            </div>
            <EntitiesFilterTooltip tabName={tabName} tableName={tableName} />
          </div>

          <div className={styles.chartContainer}>
          {graphLoading && (
            <div className={styles.loading}>Loading…</div>
          )}
            {!graphLoading && (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={data}
                  margin={{ top: 40, right: -20, bottom: 0, left: 0 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="key"
                    axisLine={false}
                    tickFormatter={(tickItem) => dateformat(tickItem, "dd-mmm")}
                  />
                  <YAxis
                    dataKey="value"
                    axisLine={false}
                    orientation="right"
                    tickLine={false}
                    domain={[0, "dataMax"]}
                  />
                  <Tooltip
                    content={(props) => (
                      <CustomTooltip
                        {...props}
                        legend={legend}
                        valueFormatter={valueFormatter}
                      />
                    )}
                    cursor={false}
                  />
                  <Bar
                    dataKey="value"
                    fill={primaryColor || "#e51058"}
                    maxBarSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div className={styles.actionWrapper}>
            <div
              className={styles.actionButton}
              onClick={() => {
                handleLocalToggle();
              }}
            >
              <i className="paperflite-right-arrow"></i>
              view more
            </div>
            </div>
            {openScrollList && (
              <div>
                <EntityInsightsListView
                  show={openScrollList}
                  title={scrollTitle || ""}
                  description={description}
                  items={items || []}
                  loading={loading || false}
                  pagination={pagination as PaginationState}
                  loadMore={loadMore}
                  onClose={handleCancelClick}
                  renderItem={renderScrollItem}
                  columns={columns}
                  titleColumnName={titleColumnName}
                  downloadConfig={downloadConfig}
                  fetchState={fetchState}
                  baseFilters={baseFilters}
                  downloadItems={download.items|| []}
                  downloadPagination={download.pagination || {}}
                  invalidateData={invalidateDownloadData}
                  firstColumn={firstColumn}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityInsightPanel;

interface EntityInsightsListViewProps {
  title: string;
  description?: string;
  items: any[];
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  onClose: () => void;
  renderItem: any;
  columns: string[];
  show: boolean;
  canDownload?: boolean;
  actionHandler?: () => void;
  downloadConfig?: DownloadConfig;
  fetchState: any;
  baseFilters?: any;
  downloadItems: any[];
  downloadPagination: any;
  invalidateData: () => void;
  firstColumn?: string;
  titleColumnName?: string;
}

interface DownloadConfig {
  name: string;
  schema: any[];
  fileName: string;
  title: string;
  reportType: string;
}

const EntityInsightsListView: React.FC<EntityInsightsListViewProps> = ({
  title,
  description,
  items,
  pagination,
  loadMore,
  onClose,
  loading,
  renderItem,
  columns,
  show,
  downloadConfig,
  fetchState,
  baseFilters,
  downloadItems,
  downloadPagination,
  invalidateData,
  firstColumn,
  titleColumnName
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const columnCount = columns.length;

  const dispatch = useDispatch();

  const handleCloseClick = () => {
    setIsClosing(true);
  };

  const handleAnimationEnd = () => {
    if (isClosing) {
      onClose();
    }
  };

  const [activeDownloadConfig, setActiveDownloadConfig] = useState<
    typeof downloadConfig | null
  >(null);

  const hasMore = () => {
    return (pagination.currentPage ?? 0) + 1 < (pagination.totalPages ?? 0);
  };

  const handleDownloadClick = () => {
    if (!downloadConfig || !baseFilters) return;
    const request = baseFilters;

    dispatch(
      intializeDownloadReport({
        id: downloadConfig.name,
        request,
        page: 0,
        limit: 50,
      }),
    );
    setActiveDownloadConfig(downloadConfig);
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      backdrop={true}
      scroll={false}
      className={cx(styles.customOffcanvas)}
    >
      <div
        className={cx(styles.scrollList, { [styles.slideOut]: isClosing })}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            {title}
            <InfoTooltip placement="right" content={description}>
              <i className={cx(styles.paperfliteInfo, "paperflite-info")} />
            </InfoTooltip>
          </div>
          <div className={styles.downloadContainer}>
            <div className={styles.downloadText}>
              {userRoleService.canDownloadReport() && downloadConfig && (
                <DownloadReports
                  title={downloadConfig.title}
                  fileName={downloadConfig.fileName}
                  schema={downloadConfig.schema}
                  data={downloadItems || []}
                  fetchState={fetchState}
                  fetchDataForPage={(pageNumber) => {
                    dispatch(
                      intializeDownloadReport({
                        id: _.get(activeDownloadConfig,"name",""),
                        request: baseFilters,
                        page: pageNumber,
                        limit: 50,
                      }),
                    );
                  }}
                  pagination={downloadPagination || {}}
                  invalidateData={invalidateData}
                  downloadClick={handleDownloadClick}
                />
              )}
            </div>
            <div className={styles.closeContainer} onClick={handleCloseClick}>
              <i className="paperflite-close" />
            </div>
          </div>
        </div>

        <div className={styles.tableList}>
          <div className={styles.tableHeader}>
            <div className={styles.tableTitle}>{titleColumnName}</div>
            <div className={styles.tableHead}>
              {columns?.map((column, index) => (
                <div key={index} className={styles.tableHeading}>
                  {column}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.scrollBody}>
            <InfiniteScroll
              className={styles.scroll}
              loading={loading}
              hasMore={hasMore()}
              loadMore={loadMore}
              loader={() => (
                <div className={styles.loader}>loading more items...</div>
              )}
            >
              {items.map((item) => renderItem({ item ,columnCount}))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </Offcanvas>
  );
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  legend: any;
  valueFormatter?: (value: any) => string;
}> = ({ active, payload, legend, valueFormatter }) => {
  if (active && payload && payload.length) {
    const payloadObj = _.get(payload, "[0].payload", null);
    if (!payloadObj) return null;

    let value = _.get(payloadObj, "value", "");
    if (valueFormatter) {
      value = valueFormatter(value);
    }

    return (
      <div className={styles.tooltip}>
        <p className="key">
          {`${legend.key} : ${dateformat(_.get(payloadObj, "key", ""), "dd-mmm-yyyy")}`}
        </p>
        <p className="value">{`${legend.value} : ${value}`}</p>
      </div>
    );
  }

  return null;
};
